/**
 * @fileoverview Toast Component - Notification System
 *
 * This component displays temporary toast notifications for user feedback.
 * Supports success, error, and info message types with auto-dismiss.
 *
 * @description
 * Key features:
 * - Auto-dismisses after 10 seconds
 * - Three visual variants (success/error/info)
 * - Manual close button
 * - Slide-in animation
 *
 * @requires React
 */
import React, { useEffect, useRef } from 'react';

/**
 * Toast Component
 *
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {'success'|'error'|'info'} props.type - The type of notification
 * @param {Function} props.onClose - Callback to close the toast
 * @param {boolean} props.persistent - If true, toast won't auto-dismiss
 * @returns {JSX.Element}
 */
function Toast({ message, type = 'success', onClose, persistent = false }) {
  /* ========================================================================
     Refs - Store onClose to avoid re-triggering effect
     ======================================================================== */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /* ========================================================================
     Effects - Auto Dismiss
     ======================================================================== */

  /**
   * Effect: Set up auto-dismiss timer
   * Closes the toast after 10 seconds (unless persistent)
   */
  useEffect(() => {
    if (persistent) return;
    const timer = setTimeout(() => onCloseRef.current(), 10000);
    return () => clearTimeout(timer);
  }, [persistent]);

  /* ========================================================================
     Render
     ======================================================================== */

  // Determine background color based on type
  const bgColor = type === 'error' ? 'bg-red-500' : type === 'info' ? 'bg-indigo-500' : 'bg-emerald-500';

  return (
    <div className={`${bgColor} text-white px-6 py-4 rounded-xl shadow-lg animate-slide-in max-w-sm flex items-start justify-between gap-3`}>
      <span className="flex-1">{message}</span>
      <button
        onClick={onClose}
        className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors"
        aria-label="Close notification"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default Toast;
